const PAGE_SIZE = 10;

interface Must {
  term?: {
    'meta.tag.code.keyword'?: string;
    'meta.tag.system.keyword'?: string;
  };
  terms?: {
    'status.keyword': Array<string>;
  };
}

const mustArray: Array<Must> = [
  {
    term: {
      'meta.tag.code.keyword': 'survey-form',
    },
  },
  {
    term: {
      'meta.tag.system.keyword': 'http://lifeomic.com/fhir/questionnaire-type',
    },
  },
  {
    term: {
      'meta.tag.code.keyword': 'LifeOmic Survey',
    },
  },
  {
    term: {
      'meta.tag.system.keyword': 'http://lifeomic.com/fhir/source',
    },
  },
];

const toContainsQueryString = (searchValue: string) => {
  const trimmed = searchValue.trim();
  if (trimmed.length === 0 || trimmed === '*') return '*';

  return trimmed
    .split(/\s+/g)
    .filter(Boolean)
    .map(token => {
      const hasLeadingWildcard = token.startsWith('*');
      const hasTrailingWildcard = token.endsWith('*');
      if (hasLeadingWildcard && hasTrailingWildcard) return token;
      if (hasLeadingWildcard) return `${token}*`;
      if (hasTrailingWildcard) return `*${token}`;
      return `*${token}*`;
    })
    .join(' ');
};

const createQueryStringFilter = (searchValue: string) => {
  const query = toContainsQueryString(searchValue);
  return [
    {
      query_string: {
        fields: ['title', 'description'],
        analyze_wildcard: true,
        query,
        default_operator: 'AND',
      },
    },
  ];
};

// Find all surveys that don't have a primary survey id.
// These would be old surveys (before versioning).
export const getOldSurveyCount = (
  status: Array<string>,
  searchValue: string,
) => {
  const must = Array.from(mustArray);

  if (status) {
    must.push({
      terms: {
        'status.keyword': status,
      },
    });
  }

  const filter = createQueryStringFilter(searchValue);

  return {
    type: 'select',
    columns: [
      {
        type: 'elasticsearch',
        aggregations: {
          total: {
            cardinality: {
              field: 'id.keyword',
            },
          },
        },
      },
    ],
    from: [
      {
        table: 'questionnaire',
      },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must,
          must_not: {
            term: {
              'meta.tag.system.keyword':
                'http://lifeomic.com/fhir/primary-survey-id',
            },
          },
          filter,
        },
      },
    },
    limit: [
      {
        type: 'number',
        value: 1,
      },
      {
        type: 'number',
        value: 1000,
      },
    ],
  };
};

// Finds all surveys that have a primary survey id.
// Then filter by latest survey version of each status
// (active, draft, retired) for that survey.
export const getLastestSurveyVersionCount = (
  status: Array<string>,
  searchValue: string,
) => {
  const must = Array.from(mustArray);

  must.push({
    term: {
      'meta.tag.system.keyword': 'http://lifeomic.com/fhir/primary-survey-id',
    },
  });

  if (status) {
    must.push({
      terms: {
        'status.keyword': status,
      },
    });
  }

  const filter = createQueryStringFilter(searchValue);

  return {
    type: 'select',
    columns: [
      {
        expr: {
          type: 'column_ref',
          column: 'meta.tag.code',
        },
      },
      {
        expr: {
          type: 'column_ref',
          column: 'meta.tag.system',
        },
      },
    ],
    from: [
      {
        table: 'questionnaire',
      },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must,
          filter,
        },
      },
    },
    limit: [
      {
        type: 'number',
        value: 0,
      },
      {
        type: 'number',
        value: 1000,
      },
    ],
  };
};

export const getSubjectCountForSurvey = (surveyId: string) => {
  if (!surveyId) return;

  return {
    type: 'select',
    columns: [
      {
        type: 'elasticsearch',
        aggregations: {
          results: {
            cardinality: {
              field: 'subject.reference.keyword',
            },
          },
        },
      },
    ],
    from: [
      {
        table: 'questionnaire_response',
      },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          should: [
            {
              term: {
                'questionnaire.reference.keyword': surveyId,
              },
            },
            {
              term: {
                'questionnaire.reference.keyword': `Questionnaire/${surveyId}`,
              },
            },
          ],
        },
      },
    },
  };
};

export const getSurveys = (
  status: Array<string>,
  searchValue: string,
  pageIndex: number,
) => {
  const must = Array.from(mustArray);

  if (status) {
    must.push({
      terms: {
        'status.keyword': status,
      },
    });
  }

  const filter = createQueryStringFilter(searchValue);

  return {
    type: 'select',
    columns: '*',
    from: [
      {
        table: 'questionnaire',
      },
    ],
    orderby: [
      {
        type: 'DESC',
        expr: {
          type: 'column_ref',
          column: 'date',
        },
      },
    ],
    where: {
      type: 'elasticsearch',
      query: {
        bool: {
          must,
          filter,
        },
      },
    },
    limit: [
      {
        type: 'number',
        value: pageIndex * PAGE_SIZE,
      },
      {
        type: 'number',
        value: PAGE_SIZE,
      },
    ],
  };
};
